import { AxiosResponse, CancelTokenSource } from 'axios';
import { Commit } from 'vuex';
import { SET_CANCEL_TOKEN } from '@/main/store/listEditTypes';
import _ from 'lodash';
import { PagesToLogStats } from '@/modules/sestavy/constants';
import { HttpRequest } from '#services/httpRequest';

export type CancelTokenKeyCommit =
	{ cancelTokenKey: string, commit: Commit } | { cancelTokenKey: null, commit: null };

export const cancelInit: CancelTokenKeyCommit = { cancelTokenKey: null, commit: null };

export function reportUrl(reportName: string) { return `/Reports/Reports/${reportName}`; }

export class BaseApi extends HttpRequest {
	public apiPath: string;
	private urlFragments: string[];
	public totalSecondsToLoad: number = 0 ;
	constructor( 
		public area: string,
		public controller: string,
		...urlFragments: string[]
	) {
		super();

		this.urlFragments = urlFragments;
		this.apiPath = `/${this.area}/${this.controller}/${this.urlFragments.join('/')}`;
	}

	schemaUrl(tableName: string) {
		return `/${this.area}/${this.controller}/${tableName}Schema`;
	}

	tableUrl(tableName: string) {
		return `/${this.area}/${this.controller}/${tableName}Rows`;
	}

	tableSchemaUrl() {
		return this.apiPath;
	}

	tableDataUrl() {
		return this.apiPath;
	}

	tableSchemaDataUrl(tableName: string) {
		return `/${this.area}/${this.controller}/${tableName}SchemaAndRows`;
	}

	tableDeleteEntityUrl(tableName: string) {
		return `/${this.area}/${this.controller}/${tableName}DeleteEntity`;
	}

	default(params?: any, { cancelTokenKey, commit }: CancelTokenKeyCommit = cancelInit) {
		const cancelPtr = cancelTokenKey ? { value: null } : null;
		const resp = this.fetch(`${this.apiPath}default`, params);

		cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });

		return resp;
	}

	async entity(id: string | number, edit?: Boolean | null, { cancelTokenKey, commit }: CancelTokenKeyCommit = cancelInit) {
	
		if (_.isNil(id) || id === 'null' || id === 'undefined') {
			return Promise.resolve({ data: {} } as AxiosResponse<any>);
		}
		const cancelPtr = cancelTokenKey ? { value: null } : null;
		const editParam = _.isNil(edit) ? '' : `?edit=${edit}`;
		const resp = await this.fetch(`${this.apiPath}${id}${editParam}`, undefined, { cancelPtr }, { errorAlert: id });

		cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });
        
		return resp;
	}

	createEntity(data?: any) {
		return this.post(`${this.apiPath}`, data);
	}

	updateEntity(id: string | number, data?: any) {
		return this.update(`${this.apiPath}${id}`, data);
	}

	deleteEntity(id: string | number) {
		return this.delete(`${this.apiPath}${id}`);
	}

	async filter({ cancelTokenKey, commit }: CancelTokenKeyCommit = cancelInit) {
		const startTime = performance.now();
		const cancelPtr = cancelTokenKey ? { value: null } : null;
		const resp = await this.fetch(`${this.apiPath}Filter`, undefined, { cancelPtr });
		cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });
		const endTime  = performance.now() - startTime;
		if([PagesToLogStats.WIP_REPORT,PagesToLogStats.TIME_CLOCKING_REPORT,PagesToLogStats.ORDER_ARCHVE_PAGE,PagesToLogStats.PART_MAITAINANCE_PAGE,PagesToLogStats.ISSUE_PARTS_SCREEN,
			PagesToLogStats.SERVICE_ORDER_PAGE,PagesToLogStats.PLANNED_ORDER_PAGE,PagesToLogStats.REAL_TIME_INVENTORY_REPORT,PagesToLogStats.PLANNED_ORDER_PAGE,
		PagesToLogStats.REVENUE_DETAILS_REPORT].includes(this.apiPath as PagesToLogStats))
		{
			let seconds = parseFloat((endTime / 1000).toFixed(2));
			this.totalSecondsToLoad = this.totalSecondsToLoad + seconds;
		}

		return resp;
	}

	async tableSchema({ cancelTokenKey, commit }: CancelTokenKeyCommit = cancelInit) {
		const startTime = performance.now();

		const cancelPtr = cancelTokenKey ? { value: null } : null;
		const resp = await this.fetch(`${this.tableSchemaUrl()}Schema`, undefined, { cancelPtr });
		cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });
		const endTime  = performance.now() - startTime;
		if([PagesToLogStats.WIP_REPORT,PagesToLogStats.TIME_CLOCKING_REPORT,PagesToLogStats.ORDER_ARCHVE_PAGE,PagesToLogStats.PART_MAITAINANCE_PAGE,PagesToLogStats.ISSUE_PARTS_SCREEN,
			PagesToLogStats.SERVICE_ORDER_PAGE,PagesToLogStats.PLANNED_ORDER_PAGE,PagesToLogStats.REAL_TIME_INVENTORY_REPORT,PagesToLogStats.PLANNED_ORDER_PAGE,
		PagesToLogStats.REVENUE_DETAILS_REPORT].includes(this.tableSchemaUrl() as PagesToLogStats))
		{
			let seconds = parseFloat((endTime / 1000).toFixed(2));
			this.totalSecondsToLoad = this.totalSecondsToLoad + seconds;
		}
		
		return resp;
	}

	async tableData(filter?: object, { cancelTokenKey, commit }: CancelTokenKeyCommit = cancelInit) {

		const startTime = performance.now();
		const cancelPtr = cancelTokenKey ? { value: null } : null;
		const rowsRequestSuffix = this.urlFragments.length > 0 ? 'Rows' : '';
		const resp = await this.fetch(`${this.tableDataUrl()}${rowsRequestSuffix}`, filter, { cancelPtr });
		cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });

		const endTime  = performance.now() - startTime;
		if([PagesToLogStats.WIP_REPORT,PagesToLogStats.TIME_CLOCKING_REPORT,PagesToLogStats.ORDER_ARCHVE_PAGE,PagesToLogStats.PART_MAITAINANCE_PAGE,PagesToLogStats.ISSUE_PARTS_SCREEN,
			PagesToLogStats.SERVICE_ORDER_PAGE,PagesToLogStats.PLANNED_ORDER_PAGE,PagesToLogStats.REAL_TIME_INVENTORY_REPORT,PagesToLogStats.PLANNED_ORDER_PAGE,
		PagesToLogStats.REVENUE_DETAILS_REPORT].includes(this.tableDataUrl() as PagesToLogStats))
		{
			let seconds = parseFloat((endTime / 1000).toFixed(2));
			this.totalSecondsToLoad = this.totalSecondsToLoad + seconds;
		}
		if(this.totalSecondsToLoad > 0){

			await this.logPerformance('LoggingPrp',this.tableDataUrl(), 'Load', this.totalSecondsToLoad);
			this.totalSecondsToLoad = 0;
		}
		return resp;
	}
    async logPerformance(loggingPrp:string, path:string, operationType:string, time:number)
	{
		await this.post(`/Performance/LogPerformance?loggingPrp=${loggingPrp}&page=${path}&operationType=${operationType}&iTotalSecondsTaken=${time}`);
	}
	rowDetail(id: string | number, { cancelTokenKey, commit }: CancelTokenKeyCommit = cancelInit) {
		const cancelPtr = cancelTokenKey ? { value: null } : null;
		const resp = this.fetch(`/${this.area}/${this.controller}/RowDetail/${id}`, undefined, { cancelPtr });

		cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });

		return resp;
	}

	filterSelect({ cancelTokenKey, commit }: CancelTokenKeyCommit = cancelInit) {
		const cancelPtr = cancelTokenKey ? { value: null } : null;
		const resp = this.fetch(`${this.apiPath}SelectFilter`, undefined, { cancelPtr });

		cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });

		return resp;
	}

	tableSchemaSelect({ cancelTokenKey, commit }: CancelTokenKeyCommit = cancelInit) {
		const cancelPtr = cancelTokenKey ? { value: null } : null;
		const resp = this.fetch(`${this.apiPath}SelectSchema`, undefined, { cancelPtr });

		cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });

		return resp;
	}

	tableDataSelect(filter: object, { cancelTokenKey, commit }: CancelTokenKeyCommit = cancelInit) {
		const cancelPtr = cancelTokenKey ? { value: null } : null;
		const resp = this.fetch(`${this.apiPath}SelectRows`, filter, { cancelPtr });

		cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });

		return resp;
	}
}

export class DependentTable {
	tableSchema: (cancel: CancelTokenKeyCommit) => Promise<AxiosResponse<any>>;
	tableData: (filter: object | undefined, cancel: CancelTokenKeyCommit) => Promise<AxiosResponse<any>>;
	tableSchemaData: (filter: object | undefined, cancel: CancelTokenKeyCommit) => Promise<AxiosResponse<any>>;
	tableDeleteEntity: (id: number) => Promise<AxiosResponse<any>>;

	constructor(public name: string, apiProvider: () => BaseApi) {
		this.tableData = (filter: object | undefined, { cancelTokenKey, commit } = cancelInit) => {
			const cancelPtr = cancelTokenKey ? { value: null } : null;
			const resp = apiProvider().fetch(apiProvider().tableUrl(this.name), filter, { cancelPtr });

			cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });

			return resp;
		};

		this.tableSchema = ({ cancelTokenKey, commit } = cancelInit) => {
			const cancelPtr = cancelTokenKey ? { value: null } : null;
			const resp = apiProvider().fetch(apiProvider().schemaUrl(this.name), undefined, { cancelPtr });

			cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });

			return resp;
		};

		this.tableSchemaData = (filter: object | undefined, { cancelTokenKey, commit } = cancelInit) => {
			const cancelPtr = cancelTokenKey ? { value: null } : null;
			const resp = apiProvider().fetch(apiProvider().tableSchemaDataUrl(this.name), { ...filter, wantRows: true }, { cancelPtr });

			cancelTokenKey && commit!(SET_CANCEL_TOKEN, { cancelTokenKey, token: cancelPtr!.value });

			return resp;
		};

		this.tableDeleteEntity = (id: number) => apiProvider().delete(`${apiProvider().tableDeleteEntityUrl(this.name)}${id}`);
	}
}
